import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const words = (s) => String(s || "").replace(/_/g, " ");
const ENTRY = {
  proposed: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  agreed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  disputed: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  handed_over: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
};

export const ECHandover = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [progress, setProgress] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [res, tpl, prog] = await Promise.all([api.get("/ec/handover"),
        api.get("/ec/handover/templates"), api.get("/ec/handover/progress")]);
      setData(res.data);
      setTemplates(tpl.data);
      setProgress(prog.data);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setDialog(null);
      onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (error) return (
    <Panel title="Handover" testId="panel-handover-closed">
      <p className="text-[12.5px] text-muted-foreground" data-testid="handover-refused">{error}</p>
    </Panel>
  );
  if (!data) return (
    <Panel title="Handover" testId="panel-handover-loading">
      <p className="text-[12.5px] text-muted-foreground">Opening the handover…</p>
    </Panel>
  );

  const h = data.handover;
  const complete = h.status === "complete";

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Handover" value={words(h.status)} testId="handover-kpi-status"
             hint={h.handover_ref} />
        <Kpi label="Departments" value={data.counters.lists} testId="handover-kpi-lists"
             hint={`${data.counters.entries} line(s) between the committees`} />
        <Kpi label="Agreed" value={data.counters.agreed} testId="handover-kpi-agreed"
             hint={`${data.counters.handed_over} handed over`} />
        <Kpi label="In dispute" value={data.counters.disputed} testId="handover-kpi-disputed"
             hint={data.counters.disputed ? "Settle these before signing off" : "Nothing disputed"} />
      </div>

      {progress && progress.overall.total > 0 && (
        <Panel title="What each incoming office bearer still has outstanding"
               testId="panel-handover-progress"
               kicker={`${progress.overall.done} of ${progress.overall.total} line(s) settled across ${progress.overall.people} office bearer(s) · ${progress.overall.percent}% done`}>
          <div className="space-y-3">
            {progress.people.map((p) => (
              <div key={p.name} data-testid={`progress-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[12.5px] font-medium text-foreground">
                    {p.name}
                    {progress.you?.name === p.name && (
                      <span className="ml-1.5 text-[10.5px] text-primary">your list</span>
                    )}
                  </span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {p.done}/{p.total} · {p.outstanding} outstanding
                    {p.disputed ? ` · ${p.disputed} in dispute` : ""}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className={`h-full rounded-full transition-all duration-500 ${
                    p.disputed ? "bg-amber-500" : "bg-emerald-500"}`}
                       style={{ width: `${p.percent}%` }} />
                </div>
                <div className="mt-1 space-y-0.5">
                  {p.departments.map((d) => (
                    <p key={d.list_ref} className="text-[11px] text-muted-foreground">
                      <span className="text-foreground">{d.department}</span> · {d.done}/{d.total}
                      {d.next_up.length > 0
                        ? ` · next up: ${d.next_up.map((n) => n.title).join("; ")}`
                        : " · nothing outstanding"}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="The handover between the committees" testId="panel-handover"
             kicker={`${(h.from_members || []).length} outgoing · ${(h.to_members || []).length} incoming · the outgoing committee keeps access until both sign off`}
             actions={(
               <div className="flex flex-wrap items-center gap-2">
                 {data.you_may_edit && (
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="add-handover-list"
                           onClick={() => setDialog({ kind: "list" })}>
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a department
                   </Button>
                 )}
                 {data.you_may_complete && (
                   <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                           data-testid="mark-handover-complete"
                           onClick={() => setDialog({ kind: "complete", note: "" })}>
                     <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Mark the handover complete
                   </Button>
                 )}
               </div>
             )}>
        <div className="space-y-1">
          {(h.completed_by || []).map((c) => (
            <p key={c.email} className="text-[12px] text-muted-foreground"
               data-testid={`handover-signed-${c.email}`}>
              <CheckCircle2 className="mr-1.5 inline h-3.5 w-3.5 text-emerald-500" />
              {c.name} — {c.office} · marked complete {c.at.slice(0, 10)}
              {c.note ? ` — ${c.note}` : ""}
            </p>
          ))}
          {!complete && (data.waiting_on || []).length > 0 && (
            <p className="text-[12px] text-muted-foreground" data-testid="handover-waiting">
              Waiting on the {data.waiting_on.join(" and the ")} to sign off.
            </p>
          )}
          {complete && (
            <p className="text-[12px] text-muted-foreground" data-testid="handover-complete-note">
              Both the incoming President and Secretary have signed off. The outgoing committee's
              access to this handover is closed and the record is kept for good.
            </p>
          )}
          {data.you_have_marked && !complete && (
            <p className="text-[11.5px] text-muted-foreground" data-testid="handover-you-marked">
              You have marked it. It completes when the other office bearer does too.
            </p>
          )}
        </div>
      </Panel>

      {data.lists.length === 0 ? (
        <Panel title="Department lists" testId="panel-handover-empty">
          <p className="text-[12.5px] text-muted-foreground" data-testid="handover-empty">
            No department has opened a handover list yet. Each department keeps its own, and the two
            committees negotiate the lines between them.
          </p>
        </Panel>
      ) : data.lists.map((lst) => (
        <Panel key={lst.list_ref} title={lst.department} testId={`handover-list-${lst.list_ref}`}
               kicker={`${lst.outgoing_name || "outgoing"} → ${lst.incoming_name || "incoming"} · ${(lst.entries || []).length} line(s)${
                 lst.checklist_used ? " · started from the checklist" : ""}`}
               actions={data.you_may_edit && (
                 <div className="flex flex-wrap gap-2">
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid={`fill-checklist-${lst.list_ref}`}
                           onClick={() => run("fill",
                             () => api.post(`/ec/handover/lists/${lst.list_ref}/checklist`),
                             "The starter checklist is on the list")}>
                     Fill from the checklist
                   </Button>
                   {data.you_may_complete !== undefined && (
                     <Button size="sm" variant="ghost" className="h-8 rounded-full"
                             data-testid={`save-checklist-${lst.list_ref}`}
                             onClick={() => run("save",
                               () => api.post(
                                 `/ec/handover/lists/${lst.list_ref}/save-as-checklist`),
                               "Kept as this department's starter checklist")}>
                       Keep as the checklist
                     </Button>
                   )}
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid={`add-entry-${lst.list_ref}`}
                           onClick={() => setDialog({ kind: "entry", list_ref: lst.list_ref })}>
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a line
                   </Button>
                 </div>
               )}>
          <div className="space-y-2">
            {(lst.entries || []).length === 0 && (
              <p className="text-[12.5px] text-muted-foreground">Nothing listed yet.</p>
            )}
            {(lst.entries || []).map((e) => (
              <div key={e.entry_ref} className="rounded-xl border border-border px-3.5 py-3"
                   data-testid={`handover-entry-${e.entry_ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="block text-[12.5px] font-medium text-foreground">
                      {e.title}
                    </span>
                    <span className="block text-[10.5px] text-muted-foreground">
                      {e.entry_ref} · raised by {e.raised_by} · {e.raised_at.slice(0, 10)}
                      {e.from_checklist ? " · from the starter checklist" : ""}
                    </span>
                  </span>
                  <Chip className={ENTRY[e.status]} testId={`entry-status-${e.entry_ref}`}>
                    {words(e.status)}
                  </Chip>
                </div>
                {e.detail && (
                  <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
                    {e.detail}
                  </p>
                )}
                <div className="mt-2 space-y-0.5">
                  {(e.comments || []).map((c, index) => (
                    <p key={`${c.at}-${index}`} className="text-[11.5px] text-muted-foreground">
                      <span className="text-foreground">{c.by}</span>: {c.text}
                      {c.now ? ` (${words(c.was)} → ${words(c.now)})` : ""} ·{" "}
                      {c.at.slice(0, 10)}
                    </p>
                  ))}
                </div>
                {data.you_may_edit && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(data.statuses).filter(([key]) => key !== e.status)
                      .map(([key, label]) => (
                        <Button key={key} size="sm" variant="outline"
                                className="h-7 rounded-full text-[11px]"
                                data-testid={`mark-${key}-${e.entry_ref}`}
                                onClick={() => setDialog({ kind: "mark", entry_ref: e.entry_ref,
                                  status: key, label, comment: "" })}>
                          {label}
                        </Button>
                      ))}
                    <Button size="sm" variant="ghost" className="h-7 rounded-full text-[11px]"
                            data-testid={`comment-${e.entry_ref}`}
                            onClick={() => setDialog({ kind: "note", entry_ref: e.entry_ref,
                              text: "" })}>
                      Comment
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}

      <Dialog open={dialog?.kind === "list"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="handover-list-dialog">
          <DialogHeader><DialogTitle>Open a department handover list</DialogTitle>
            <DialogDescription>
              Each department keeps its own list. Both committees can add to it and settle the lines
              between them.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[11.5px] text-muted-foreground">Department</Label>
              <Select value={dialog?.department || ""}
                      onValueChange={(v) => setDialog({ ...dialog, department: v })}>
                <SelectTrigger data-testid="handover-department-pick">
                  <SelectValue placeholder="Pick a department, or type your own below" />
                </SelectTrigger>
                <SelectContent>
                  {(templates?.items || []).map((t) => (
                    <SelectItem key={t.department} value={t.department}>
                      {t.department} — {t.lines} line starter checklist ({t.source})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input data-testid="handover-department" value={dialog?.department || ""}
                     placeholder="Or a department of your own"
                     onChange={(e) => setDialog({ ...dialog, department: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">Handing over</Label>
                <Input data-testid="handover-outgoing" value={dialog?.outgoing_name || ""}
                       onChange={(e) => setDialog({ ...dialog,
                         outgoing_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11.5px] text-muted-foreground">Taking over</Label>
                <Input data-testid="handover-incoming" value={dialog?.incoming_name || ""}
                       onChange={(e) => setDialog({ ...dialog,
                         incoming_name: e.target.value })} />
              </div>
            </div>
            <label className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
              <input type="checkbox" data-testid="use-checklist" className="mt-0.5"
                     checked={dialog?.use_checklist !== false}
                     onChange={(e) => setDialog({ ...dialog,
                       use_checklist: e.target.checked })} />
              <span>
                Start from the checklist — the department's own lines plus the six every post
                inherits: passwords and logins, files and records, outstanding commitments, the
                trust's external professional contacts, introductions to them, and a knowledge
                transfer session.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="handover-list-save"
                    disabled={busy === "list" || (dialog?.department || "").trim().length < 3}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("list", () => api.post("/ec/handover/lists", {
                      department: dialog.department,
                      outgoing_name: dialog.outgoing_name || "",
                      incoming_name: dialog.incoming_name || "",
                      use_checklist: dialog.use_checklist !== false }), "The list is open")}>
              {busy === "list" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Open the list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "entry"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="handover-entry-dialog">
          <DialogHeader><DialogTitle>Add a line to the handover</DialogTitle>
            <DialogDescription>
              Anything the next office bearer must be given, told or shown.
            </DialogDescription></DialogHeader>
          <Input data-testid="entry-title" value={dialog?.title || ""}
                 placeholder="Keys to the cultural store"
                 onChange={(e) => setDialog({ ...dialog, title: e.target.value })} />
          <Textarea rows={3} data-testid="entry-detail" placeholder="What exactly, and where"
                    value={dialog?.detail || ""}
                    onChange={(e) => setDialog({ ...dialog, detail: e.target.value })} />
          <DialogFooter>
            <Button data-testid="entry-save"
                    disabled={busy === "entry" || (dialog?.title || "").trim().length < 4}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("entry",
                      () => api.post(`/ec/handover/lists/${dialog.list_ref}/entries`,
                        { title: dialog.title, detail: dialog.detail || "" }),
                      "The line is on the list")}>
              {busy === "entry" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add the line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "mark"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="handover-mark-dialog">
          <DialogHeader><DialogTitle>Mark this line {dialog?.label?.toLowerCase()}</DialogTitle>
            <DialogDescription>
              Your words go on the record for the other committee to read. A dispute must say what
              is wrong.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="mark-comment" value={dialog?.comment || ""}
                    placeholder="Anything the other committee should know"
                    onChange={(e) => setDialog({ ...dialog, comment: e.target.value })} />
          <DialogFooter>
            <Button data-testid="mark-save"
                    disabled={busy === "mark" || (dialog?.status === "disputed"
                      && (dialog?.comment || "").trim().length < 5)}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("mark",
                      () => api.post(`/ec/handover/entries/${dialog.entry_ref}/status`,
                        { status: dialog.status, comment: dialog.comment || "" }),
                      "The line is updated")}>
              {busy === "mark" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "note"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="handover-note-dialog">
          <DialogHeader><DialogTitle>Comment on this line</DialogTitle>
            <DialogDescription>Negotiate in the open; nothing here is ever deleted.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} data-testid="note-text" value={dialog?.text || ""}
                    onChange={(e) => setDialog({ ...dialog, text: e.target.value })} />
          <DialogFooter>
            <Button data-testid="note-save"
                    disabled={busy === "note" || (dialog?.text || "").trim().length < 2}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("note",
                      () => api.post(`/ec/handover/entries/${dialog.entry_ref}/comments`,
                        { text: dialog.text }), "Your comment is on the record")}>
              {busy === "note" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add the comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "complete"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="handover-complete-dialog">
          <DialogHeader><DialogTitle>Mark the handover complete</DialogTitle>
            <DialogDescription>
              The handover closes only when both the incoming President and the incoming Secretary
              have marked it. Nothing in dispute may be left open.
            </DialogDescription></DialogHeader>
          <Textarea rows={3} data-testid="complete-note" value={dialog?.note || ""}
                    placeholder="Anything for the record"
                    onChange={(e) => setDialog({ ...dialog, note: e.target.value })} />
          <DialogFooter>
            <Button data-testid="complete-save" disabled={busy === "complete"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("complete",
                      () => api.post("/ec/handover/complete", { note: dialog.note || "" }),
                      "Your sign-off is recorded")}>
              {busy === "complete" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign it off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
